export const GIFT_PROPERTY = '_free_gift';
export const TIER_PROPERTY = '_gift_tier';
export const CHOICE_PREFIX = 'convert:gift-choice:';
export const PICK_PREFIX = 'convert:gift-pick:';
export const OPT_OUT_KEY = 'convert:gift-optout';
export const CHARGED_KEY = 'convert:gift-charged';
export const GIFT_MEMORY_MS = 10 * 60 * 1000;
export const ADDED_KEY = 'convert:gift-added';
export const REVEAL_KEY = 'convert:gift-revealed';
export const PICK_ATTRIBUTE_PREFIX = '_gift_tier_';
export const OPT_OUT_ATTRIBUTE = '_gift_optout';
export const GIFT_MODES = ['choice', 'specific', 'random', 'perk'];
const MAX_ATTEMPTS = 2;
const MAX_PASSES = 4;
const REVEAL_DELAY = 700;

export function giftTierOf(item) {
  const value = item?.properties?.[GIFT_PROPERTY];
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

export function isGiftLine(item) {
  return giftTierOf(item) !== null;
}

// SethScales Insights feature events for one cart change: gift lines added, swapped or removed (by tier) and gift
// tiers newly reached. Returns [name, data] pairs.
export function giftEvents(prev, next, tiers = [], rate = 1) {
  const byTier = (cart) => {
    const lines = new Map();
    for (const item of cart?.items || []) {
      const tier = giftTierOf(item);
      if (tier !== null && Number(item.quantity) > 0) lines.set(tier, Number(item.variant_id));
    }
    return lines;
  };
  const before = byTier(prev);
  const after = byTier(next);
  const events = [];
  for (const [tier, variant] of after) {
    if (!before.has(tier)) events.push(['gift_added', { tier, variant }]);
    else if (before.get(tier) !== variant) events.push(['gift_changed', { tier, variant, from: before.get(tier) }]);
  }
  for (const [tier, variant] of before) if (!after.has(tier)) events.push(['gift_removed', { tier, variant }]);
  if (tiers.length) {
    const reached = (cart) => computeGiftState(cart, tiers, {}, { rate }).reachedTiers;
    const had = new Set(reached(prev).map((tier) => tier.index));
    for (const tier of reached(next)) if (!had.has(tier.index)) events.push(['milestone_reached', { tier: tier.index, at: tier.threshold, bar: 'gift' }]);
  }
  return events;
}

function toId(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function lineTotal(item) {
  if (Number.isFinite(item?.final_line_price)) return item.final_line_price;
  if (Number.isFinite(item?.line_price)) return item.line_price;
  return (Number(item?.final_price ?? item?.price) || 0) * (Number(item?.quantity) || 0);
}

function originalLineTotal(item) {
  if (Number.isFinite(item?.original_line_price)) return item.original_line_price;
  if (Number.isFinite(item?.line_price)) return item.line_price;
  return (Number(item?.original_price ?? item?.price ?? item?.final_price) || 0) * (Number(item?.quantity) || 0);
}

function unitPrice(item) {
  const quantity = Number(item?.quantity) || 0;
  if (Number.isFinite(item?.original_price)) return item.original_price;
  return quantity > 0 ? originalLineTotal(item) / quantity : 0;
}

// A line Shopify already discounts by at least one full unit (e.g. an automatic "free item"
// discount on a product the customer added themselves).
export function isDiscountedFree(item) {
  const unit = unitPrice(item);
  if (!(unit > 0) || !(Number(item?.quantity) > 0)) return false;
  return originalLineTotal(item) - lineTotal(item) >= unit - 1;
}

export function tierProductIds(tier) {
  const ids = new Set();
  for (const id of tier?.productIds || []) if (toId(id)) ids.add(toId(id));
  for (const product of tier?.products || []) if (toId(product?.id)) ids.add(toId(product.id));
  for (const entry of tier?.candidates || []) {
    const id = toId(entry?.productId ?? (Array.isArray(entry?.variants) ? entry.id : null));
    if (id) ids.add(id);
  }
  return ids;
}

export function tierMode(tier) {
  return GIFT_MODES.includes(tier?.mode) ? tier.mode : 'choice';
}

export function tierHasCatalog(tier) {
  if (!tier) return false;
  const mode = tierMode(tier);
  if (mode === 'perk') return true;
  if (mode === 'random') return Array.isArray(tier.candidates) && tier.candidates.length > 0;
  return Array.isArray(tier.products) && tier.products.length > 0;
}

export function isActiveTier(tier) {
  if (!tier || tier.enabled === false) return false;
  const threshold = Number(tier.threshold);
  if (!Number.isFinite(threshold) || threshold <= 0) return false;
  if (!tier.label) return false;
  return tierHasCatalog(tier);
}

export function normalizeTiers(tiers = [], rate = 1) {
  const factor = Number.isFinite(Number(rate)) && Number(rate) > 0 ? Number(rate) : 1;
  return (Array.isArray(tiers) ? tiers : [])
    .filter(isActiveTier)
    .filter((tier) => tier && tier.threshold !== null && tier.threshold !== '' && Number.isFinite(Number(tier.threshold)))
    .map((tier, position) => ({
      ...tier,
      index: tier.index ?? position + 1,
      threshold: Math.max(0, Math.round(Number(tier.threshold) * factor)),
      mode: tierMode(tier),
      products: Array.isArray(tier.products) ? tier.products : [],
      candidates: Array.isArray(tier.candidates) ? tier.candidates : [],
    }))
    .sort((a, b) => a.threshold - b.threshold);
}

function tierVariants(tier) {
  const variants = new Map();
  for (const product of tier.products) {
    const list = Array.isArray(product.variants) && product.variants.length
      ? product.variants
      : [{ id: product.variantId, available: product.available }];
    for (const variant of list) {
      const id = toId(variant?.id);
      if (!id || variants.has(id)) continue;
      variants.set(id, { variantId: id, productId: toId(product.id), available: variant.available !== false && product.available !== false });
    }
    const primary = toId(product.variantId);
    if (primary && variants.has(primary) && product.available === false) variants.get(primary).available = false;
  }
  return variants;
}

function candidateVariants(tier) {
  const variants = new Map();
  for (const entry of tier.candidates) {
    const nested = Array.isArray(entry?.variants);
    const productId = toId(entry?.productId ?? (nested ? entry.id : null));
    const list = nested ? entry.variants : [entry];
    for (const variant of list) {
      const id = toId(variant?.id);
      if (!id || variants.has(id)) continue;
      variants.set(id, { variantId: id, productId, available: variant.available !== false && entry.available !== false });
    }
  }
  return variants;
}

function defaultVariant(tier, variants, blocked) {
  for (const product of tier.products) {
    const preferred = toId(product.variantId);
    const candidates = [preferred, ...[...variants.values()].filter((v) => v.productId === toId(product.id)).map((v) => v.variantId)];
    for (const id of candidates) {
      const entry = id && variants.get(id);
      if (entry && entry.available && !blocked.has(id)) return entry;
    }
  }
  return null;
}

export function secureRandom() {
  const source = globalThis.crypto;
  if (source?.getRandomValues) {
    const buffer = new Uint32Array(1);
    source.getRandomValues(buffer);
    return buffer[0] / 4294967296;
  }
  return Math.random();
}

function randomIndex(rng, length) {
  const value = Number(typeof rng === 'function' ? rng() : secureRandom());
  const fraction = Number.isFinite(value) ? value : 0;
  return Math.min(length - 1, Math.max(0, Math.floor(fraction * length)));
}

export function randomPick(variants, { blocked = new Set(), avoid = new Set(), rng = secureRandom } = {}) {
  const usable = [...variants.values()].filter((entry) => entry.available && !blocked.has(entry.variantId));
  const fresh = usable.filter((entry) => !avoid.has(entry.variantId));
  const pool = fresh.length ? fresh : usable;
  if (!pool.length) return null;
  const groups = new Map();
  for (const entry of pool) {
    const key = entry.productId ?? `variant-${entry.variantId}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  }
  const products = [...groups.values()];
  const product = products[randomIndex(rng, products.length)];
  return product.length > 1 ? product[randomIndex(rng, product.length)] : product[0];
}

export function giftLineProperties(tier, labelName = 'Gift') {
  const properties = { [TIER_PROPERTY]: String(tier?.index ?? '') };
  const label = String(tier?.label ?? '').trim();
  if (label && labelName) properties[labelName] = label;
  return properties;
}

export function parsePickAttribute(value) {
  if (value === undefined || value === null) return null;
  return toId(Number.parseInt(String(value), 10));
}

export function formatPickAttribute(variantId, title = '') {
  const id = toId(variantId);
  if (!id) return '';
  return title ? `${id} | ${title}` : String(id);
}

export function parseOptOut(value) {
  return [...new Set(String(value ?? '').split(',').map((part) => part.trim()).filter(Boolean))];
}

export function candidateTitle(tier, variantId) {
  const id = toId(variantId);
  for (const entry of tier?.candidates || []) {
    const list = Array.isArray(entry?.variants) ? entry.variants : [entry];
    const variant = list.find((item) => toId(item?.id) === id);
    if (!variant) continue;
    const title = String(entry.title ?? '').trim();
    const option = String(variant.title ?? '').trim();
    return option && option !== 'Default Title' && variant !== entry ? `${title} / ${option}` : title;
  }
  return '';
}

function trackPosition(sorted, subtotal) {
  if (!sorted.length) return 0;
  const max = sorted[sorted.length - 1].threshold;
  if (max <= 0) return 0;
  return Math.min(100, (subtotal / max) * 100);
}

const round2 = (value) => Math.round(value * 100) / 100;

export function computeGiftState(cart, tiers, choices = {}, options = {}) {
  const { autoAdd = true, unavailable = [], rate = 1, picks: storedPicks = {}, rng = secureRandom, optOut = [], charged = [], properties } = options;
  const blocked = new Set([...unavailable].map(Number));
  const optedOutKeys = new Set([...optOut].map(String));
  const chargedKeys = new Set([...charged].map(String));
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const giftLines = items.filter(isGiftLine);
  const paidLines = items.filter((item) => !isGiftLine(item));
  const giftTotal = giftLines.reduce((sum, item) => sum + lineTotal(item), 0);
  const baseSubtotal = Number.isFinite(cart?.items_subtotal_price)
    ? cart.items_subtotal_price - giftTotal
    : paidLines.reduce((sum, item) => sum + lineTotal(item), 0);
  const eligibleSubtotal = Math.max(0, Math.round(baseSubtotal));
  const hasPaidItems = paidLines.some((item) => Number(item.quantity) > 0);

  const sorted = normalizeTiers(tiers, rate);
  const maxThreshold = sorted.length ? sorted[sorted.length - 1].threshold : 0;
  const reachedTiers = hasPaidItems ? sorted.filter((tier) => tier.threshold <= eligibleSubtotal) : [];
  const nextTier = sorted.find((tier) => !reachedTiers.includes(tier)) || null;
  const remaining = nextTier ? Math.max(0, nextTier.threshold - eligibleSubtotal) : 0;
  let progressPercent = 0;
  if (maxThreshold > 0) progressPercent = Math.min(100, (eligibleSubtotal / maxThreshold) * 100);
  else if (sorted.length && hasPaidItems) progressPercent = 100;
  progressPercent = round2(progressPercent);
  const trackPercent = hasPaidItems || eligibleSubtotal > 0 ? round2(trackPosition(sorted, hasPaidItems ? eligibleSubtotal : 0)) : 0;

  const desiredGiftLines = [];
  const tierByKey = new Map();
  const picks = {};
  const optedOut = [];
  const chargedTiers = [];
  const coveredTiers = {};
  const claimed = new Set();
  const used = new Set();
  const usable = (entry) => Boolean(entry && entry.available && !blocked.has(entry.variantId));
  for (const tier of reachedTiers) {
    const key = String(tier.index);
    const random = tier.mode === 'random';
    if (random ? !tier.candidates.length : !tier.products.length) continue;
    if (optedOutKeys.has(key)) {
      optedOut.push(tier.index);
      continue;
    }
    if (chargedKeys.has(key)) {
      chargedTiers.push(tier.index);
      continue;
    }
    const eligibleIds = tierProductIds(tier);
    const covering = paidLines.find((item) => !claimed.has(item.key) && eligibleIds.has(toId(item.product_id)) && isDiscountedFree(item));
    if (covering) {
      claimed.add(covering.key);
      coveredTiers[key] = covering.key;
      continue;
    }
    const variants = random ? candidateVariants(tier) : tierVariants(tier);
    const existing = giftLines
      .filter((item) => giftTierOf(item) === key)
      .map((item) => variants.get(toId(item.variant_id)))
      .find(Boolean);
    let pick = null;
    if (random) {
      const stored = variants.get(toId(storedPicks?.[tier.index] ?? storedPicks?.[key]));
      if (usable(stored)) pick = stored;
      else if (usable(existing)) pick = existing;
      else pick = randomPick(variants, { blocked, avoid: used, rng });
      if (pick) picks[key] = pick.variantId;
    } else {
      const stored = variants.get(toId(choices?.[tier.index] ?? choices?.[key]));
      if (usable(stored)) pick = stored;
      else if (usable(existing)) pick = existing;
      else if (autoAdd || tier.mode === 'specific' || existing) pick = defaultVariant(tier, variants, blocked);
    }
    if (!pick) continue;
    used.add(pick.variantId);
    tierByKey.set(key, tier);
    desiredGiftLines.push({ tierIndex: tier.index, variantId: pick.variantId, productId: pick.productId });
  }

  const toRemove = [];
  const toNormalize = [];
  const satisfied = new Set();
  const chargedLines = [];
  for (const item of giftLines) {
    const tierKey = giftTierOf(item);
    const desired = desiredGiftLines.find((line) => String(line.tierIndex) === tierKey);
    if (!desired || desired.variantId !== toId(item.variant_id) || satisfied.has(tierKey)) {
      toRemove.push(item.key);
      continue;
    }
    satisfied.add(tierKey);
    if (lineTotal(item) > 0 && Number(item.quantity) === 1) {
      chargedLines.push({ key: item.key, tierIndex: Number(tierKey), variantId: toId(item.variant_id), price: lineTotal(item) });
      chargedTiers.push(Number(tierKey));
      toRemove.push(item.key);
      continue;
    }
    if (Number(item.quantity) !== 1) toNormalize.push(item.key);
  }

  // Reached tiers whose gift isn't actually in the cart for free: charged and removed, opted out, or not added yet.
  const chargedSet = new Set(chargedTiers.map(String));
  const freeGiftTiers = new Set(
    giftLines.filter((item) => lineTotal(item) === 0 && Number(item.quantity) > 0).map((item) => giftTierOf(item))
  );
  const missingTiers = reachedTiers
    .filter((tier) => (tier.mode === 'random' ? tier.candidates.length : tier.products.length))
    .filter((tier) => {
      const key = String(tier.index);
      if (coveredTiers[key]) return false;
      return chargedSet.has(key) || optedOut.includes(tier.index) || !freeGiftTiers.has(key);
    })
    .map((tier) => tier.index);

  const toAdd = desiredGiftLines
    .filter((line) => !satisfied.has(String(line.tierIndex)))
    .map((line) => {
      const key = String(line.tierIndex);
      const extra = typeof properties === 'function' ? properties(tierByKey.get(key)) : null;
      return { id: line.variantId, quantity: 1, properties: { ...(extra || {}), [GIFT_PROPERTY]: key } };
    });

  return {
    eligibleSubtotal,
    maxThreshold,
    tiers: sorted,
    reachedTiers,
    nextTier,
    remaining,
    progressPercent,
    trackPercent,
    desiredGiftLines,
    giftLines,
    toAdd,
    toRemove,
    toNormalize,
    picks,
    optedOut,
    chargedTiers,
    chargedLines,
    coveredTiers,
    missingTiers,
    freeGiftTiers: [...freeGiftTiers],
  };
}

export function milestoneMessage(state, settings = {}, formatMoney = (cents) => String(cents)) {
  if (state.nextTier) {
    return String(settings.giftProgressText || '')
      .replace(/\[amount\]/g, formatMoney(state.remaining))
      .replace(/\[gift\]/g, state.nextTier.label || '');
  }
  if (!state.reachedTiers.length) return '';
  const missing = state.missingTiers?.length ? state.tiers.find((tier) => tier.index === state.missingTiers[0]) : null;
  if (missing) return String(settings.giftQualifiedText || '').replace(/\[gift\]/g, missing.label || '');
  return String(settings.giftCompleteText || '');
}

export function formatShortMoney(cents, formatMoney, moneyFormat = '${{amount}}') {
  if (typeof formatMoney !== 'function') return String(cents);
  if (Number(cents) % 100 !== 0) return formatMoney(cents, moneyFormat);
  const whole = String(moneyFormat)
    .replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}')
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, '{{amount_no_decimals_with_comma_separator}}')
    .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, '{{amount_no_decimals_with_space_separator}}');
  return formatMoney(cents, whole);
}

export function currencyRate(win = typeof window !== 'undefined' ? window : {}) {
  const rate = Number(win?.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

function store(kind) {
  try {
    return window[kind] || null;
  } catch (error) {
    return null;
  }
}

function readJSON(storage, key) {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeJSON(storage, key, value) {
  try {
    storage?.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
}

const cartToken = (cart) => String(cart?.token ?? '');

function readChoices(tiers) {
  const choices = {};
  for (const tier of tiers) {
    try {
      const value = window.localStorage.getItem(`${CHOICE_PREFIX}${tier.index}`);
      if (value) choices[tier.index] = value;
    } catch (error) {
      return choices;
    }
  }
  return choices;
}

function saveChoice(tierIndex, variantId) {
  try {
    window.localStorage.setItem(`${CHOICE_PREFIX}${tierIndex}`, String(variantId));
  } catch (error) {
    return false;
  }
  return true;
}

function readPicks(cart, tiers) {
  const picks = {};
  const attributes = cart?.attributes || {};
  for (const tier of tiers) {
    if (tier.mode !== 'random') continue;
    const fromAttribute = parsePickAttribute(attributes[`${PICK_ATTRIBUTE_PREFIX}${tier.index}`]);
    if (fromAttribute) {
      picks[tier.index] = fromAttribute;
      continue;
    }
    const cached = readJSON(store('sessionStorage'), `${PICK_PREFIX}${tier.index}`);
    if (cached && cached.token === cartToken(cart) && toId(cached.id)) picks[tier.index] = toId(cached.id);
  }
  return picks;
}

function savePick(cart, tierIndex, variantId) {
  writeJSON(store('sessionStorage'), `${PICK_PREFIX}${tierIndex}`, { token: cartToken(cart), id: variantId });
}

function initGifts() {
  const Theme = window.Theme || {};
  const settings = Theme.settings || {};
  const rawTiers = settings.giftTiers || [];
  const failedVariants = new Set();
  const attempts = new Map();
  const attributeWrites = new Map();
  const chooserOpen = new Map();
  const revealStarted = new Map();
  let latestCart = Theme.cart?.state || null;
  let trackedCart = latestCart;
  let running = false;
  let pending = false;

  const tiers = () => normalizeTiers(rawTiers, currencyRate());
  const short = (cents) =>
    formatShortMoney(cents, typeof Theme.formatMoney === 'function' ? (value, format) => Theme.formatMoney(value, format) : null, Theme.moneyFormat);
  const labelName = () => Theme.strings?.giftProperty || 'Gift';

  function readOptOut(cart) {
    const cached = readJSON(store('sessionStorage'), OPT_OUT_KEY);
    if (cached && cached.token === cartToken(cart) && Array.isArray(cached.tiers)) return cached.tiers.map(String);
    return parseOptOut(cart?.attributes?.[OPT_OUT_ATTRIBUTE]);
  }

  function setOptOut(tierIndex, optedOut) {
    const cart = latestCart || Theme.cart?.state || null;
    const current = new Set(readOptOut(cart));
    if (optedOut) current.add(String(tierIndex));
    else current.delete(String(tierIndex));
    writeJSON(store('sessionStorage'), OPT_OUT_KEY, { token: cartToken(cart), tiers: [...current].sort() });
  }

  // Suppress a charged tier only while the shopper's paid lines are unchanged and for at most GIFT_MEMORY_MS,
  // so a retry happens once the merchant fixes the discount even if the cart stays the same.
  const fresh = (cached) => Number(cached.at) > 0 && Date.now() - Number(cached.at) < GIFT_MEMORY_MS;
  const paidSignature = (cart) =>
    (cart?.items || []).filter((item) => !isGiftLine(item)).map((item) => `${item.variant_id}x${item.quantity}`).sort().join(',');

  function readCharged(cart) {
    const cached = readJSON(store('sessionStorage'), CHARGED_KEY);
    return cached && fresh(cached) && cached.token === cartToken(cart) && cached.signature === paidSignature(cart) && Array.isArray(cached.tiers)
      ? cached.tiers.map(String)
      : [];
  }

  function rememberCharged(cart, state) {
    if (!state.chargedLines.length) return;
    const current = new Set(readCharged(cart));
    state.chargedLines.forEach((line) => current.add(String(line.tierIndex)));
    writeJSON(store('sessionStorage'), CHARGED_KEY, { token: cartToken(cart), signature: paidSignature(cart), tiers: [...current].sort(), at: Date.now() });
    if (window.Shopify?.designMode) {
      chargedNotices.push(...state.chargedLines);
      showChargedWarning();
    }
  }

  // Every gift add fires product_added_to_cart, so a page load never repeats an add already tried for the same paid lines.
  const addKey = (item) => `${item.properties?.[GIFT_PROPERTY]}:${item.id}`;
  function readAdded(cart) {
    const cached = readJSON(store('sessionStorage'), ADDED_KEY);
    return new Set(
      cached && fresh(cached) && cached.token === cartToken(cart) && cached.signature === paidSignature(cart) && Array.isArray(cached.adds) ? cached.adds : []
    );
  }

  function rememberAdded(cart, items) {
    const adds = readAdded(cart);
    items.forEach((item) => adds.add(addKey(item)));
    writeJSON(store('sessionStorage'), ADDED_KEY, { token: cartToken(cart), signature: paidSignature(cart), adds: [...adds], at: Date.now() });
  }

  const chargedNotices = [];
  function showChargedWarning(scope = document) {
    if (!window.Shopify?.designMode || !chargedNotices.length) return;
    const last = chargedNotices[chargedNotices.length - 1];
    const tier = tiers().find((entry) => entry.index === last.tierIndex);
    const money = (cents) => (Theme.formatMoney ? Theme.formatMoney(cents) : String(cents));
    const decode = (value) => {
      const area = document.createElement('textarea');
      area.innerHTML = value;
      return area.value;
    };
    const text = decode(
      Theme.strings?.giftCharged ||
        'Shopify charged [price] for the free gift "[gift]", so the theme removed it. Line item properties can\'t be discounted natively: put gift-only products in a hidden "Free gifts" collection and add an automatic Buy X get Y discount (buys: minimum amount [threshold] from "Paid catalog"; gets: 1 item from "Free gifts" at 100% off; once per order). The tier threshold must equal that minimum.'
    )
      .replace('[price]', money(last.price))
      .replace('[gift]', tier?.label || '')
      .replace('[threshold]', tier ? money(tier.threshold) : 'the tier threshold');
    scope.querySelectorAll('[data-gift-warning]').forEach((node) => {
      node.textContent = text;
      node.hidden = false;
    });
    console.warn(`[gifts] ${text}`);
  }

  const stateFor = (cart) =>
    computeGiftState(cart, rawTiers, readChoices(tiers()), {
      autoAdd: settings.giftsAutoAdd !== false,
      unavailable: failedVariants,
      rate: currencyRate(),
      picks: readPicks(cart, tiers()),
      optOut: readOptOut(cart),
      charged: readCharged(cart),
      properties: (tier) => giftLineProperties(tier, labelName()),
    });

  function pendingAttributes(cart, state) {
    const attributes = {};
    const current = cart?.attributes || {};
    for (const [key, variantId] of Object.entries(state.picks)) {
      savePick(cart, key, variantId);
      const name = `${PICK_ATTRIBUTE_PREFIX}${key}`;
      if (parsePickAttribute(current[name]) === variantId) continue;
      const tier = state.tiers.find((entry) => String(entry.index) === key);
      attributes[name] = formatPickAttribute(variantId, candidateTitle(tier, variantId));
    }
    const optOut = readOptOut(cart).sort().join(',');
    if (String(current[OPT_OUT_ATTRIBUTE] ?? '') !== optOut) attributes[OPT_OUT_ATTRIBUTE] = optOut;
    for (const [name, value] of Object.entries(attributes)) {
      const signature = `${name}=${value}`;
      const count = (attributeWrites.get(signature) || 0) + 1;
      attributeWrites.set(signature, count);
      if (count > MAX_ATTEMPTS) delete attributes[name];
    }
    return attributes;
  }

  function updateMilestones(cart, scope = document) {
    if (!cart || !rawTiers.length) return;
    const state = stateFor(cart);
    const reached = new Set(state.reachedTiers.map((tier) => String(tier.index)));
    scope.querySelectorAll('[data-cart-milestones]').forEach((root) => {
      const texts = { ...settings, giftProgressText: root.dataset.progressText, giftCompleteText: root.dataset.completeText };
      root.querySelector('[data-milestones-fill]')?.style.setProperty('--progress', String(state.trackPercent / 100));
      const track = root.querySelector('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(state.trackPercent)));
        track.setAttribute('aria-valuetext', milestoneMessage(state, texts, short) || short(state.eligibleSubtotal));
      }
      root.querySelectorAll('[data-milestone-node]').forEach((node) => {
        const tierKey = node.dataset.tier;
        const tier = state.tiers.find((entry) => String(entry.index) === tierKey);
        node.classList.toggle('is-reached', reached.has(tierKey));
        node.classList.toggle('is-next', String(state.nextTier?.index) === tierKey);
        const amount = node.querySelector('[data-milestone-amount]');
        if (amount && tier) amount.textContent = short(tier.threshold);
      });
      const message = root.querySelector('[data-milestones-message]');
      const text = milestoneMessage(state, texts, short);
      if (message && text && message.textContent.trim() !== text) message.textContent = text;
      root.classList.toggle('is-complete', !state.nextTier && reached.size > 0 && !state.missingTiers.length);
    });
    scope.querySelectorAll('[data-gift-chooser]').forEach((chooser) => {
      const tierKey = chooser.dataset.tier;
      chooser.hidden = !reached.has(tierKey) || Boolean(state.coveredTiers[tierKey]) || state.chargedTiers.includes(Number(tierKey));
      const inCart = state.freeGiftTiers.includes(tierKey) && !state.chargedTiers.includes(Number(tierKey));
      const desired = inCart ? state.desiredGiftLines.find((line) => String(line.tierIndex) === tierKey) : null;
      chooser.querySelectorAll('[data-gift-choice]').forEach((button) => {
        const selected = Boolean(desired) && Number(button.dataset.productId) === desired.productId;
        button.setAttribute('aria-pressed', String(selected));
      });
      chooser.toggleAttribute('aria-busy', running);
    });
  }

  function restoreChoosers(scope = document) {
    scope.querySelectorAll('[data-gift-chooser-details]').forEach((details) => {
      if (chooserOpen.get(details.dataset.tier)) details.open = true;
    });
  }

  function revealedSet() {
    const list = readJSON(store('sessionStorage'), REVEAL_KEY);
    return new Set(Array.isArray(list) ? list : []);
  }

  function markRevealed(id) {
    const seen = revealedSet();
    seen.add(id);
    writeJSON(store('sessionStorage'), REVEAL_KEY, [...seen].slice(-20));
  }

  function runReveals(scope = document) {
    const seen = revealedSet();
    scope.querySelectorAll('[data-gift-reveal]').forEach((line) => {
      const id = line.dataset.giftReveal;
      if (!id || line.classList.contains('is-revealed')) return;
      if (seen.has(id)) {
        line.classList.add('is-revealed', 'is-instant');
        return;
      }
      const dialog = line.closest('dialog');
      if (dialog && !dialog.open) return;
      if (line.dataset.revealScheduled) return;
      line.dataset.revealScheduled = 'true';
      const now = Date.now();
      if (!revealStarted.has(id)) revealStarted.set(id, now);
      const wait = Math.max(0, REVEAL_DELAY - (now - revealStarted.get(id)));
      setTimeout(() => {
        line.classList.add('is-revealed');
        markRevealed(id);
      }, wait);
    });
  }

  function setBusy(busy) {
    document.querySelectorAll('[data-gift-chooser]').forEach((chooser) => chooser.toggleAttribute('aria-busy', busy));
    document.dispatchEvent(new CustomEvent('gifts:busy', { detail: { busy } }));
  }

  function trackAttempt(item) {
    const key = `${item.properties[GIFT_PROPERTY]}:${item.id}`;
    const count = (attempts.get(key) || 0) + 1;
    attempts.set(key, count);
    if (count > MAX_ATTEMPTS) {
      failedVariants.add(item.id);
      return false;
    }
    return true;
  }

  function settleAttempts(cart) {
    for (const item of (cart?.items || []).filter(isGiftLine)) attempts.delete(`${giftTierOf(item)}:${item.variant_id}`);
  }

  let runPromise = Promise.resolve();

  function addGifts(items) {
    const options = { open: false, source: 'gifts', toast: false };
    return Theme.cart.add(items, options);
  }

  function trackGifts(cart) {
    if (!window.SSI || !cart) return;
    const prev = trackedCart;
    trackedCart = cart;
    if (!prev) return;
    try {
      giftEvents(prev, cart, settings.giftsEnabled ? rawTiers : [], currencyRate()).forEach(([name, data]) => Theme.ss?.('feat', name, data));
    } catch (error) {
      /* analytics must never break gifts */
    }
  }

  function reconcile({ boot = false } = {}) {
    if (!settings.giftsEnabled || !rawTiers.length || !Theme.cart || Theme.abOff?.('gift_bar')) return Promise.resolve();
    if (running) {
      pending = true;
      return runPromise;
    }
    running = true;
    runPromise = runReconcile(boot);
    return runPromise;
  }

  async function idle() {
    while (running) await runPromise;
  }

  async function runReconcile(boot = false) {
    setBusy(true);
    try {
      for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        pending = false;
        const cart = latestCart || (await Theme.cart.get());
        latestCart = cart;
        settleAttempts(cart);
        const state = stateFor(cart);
        rememberCharged(cart, state);
        const updates = {};
        state.toRemove.forEach((key) => (updates[key] = 0));
        state.toNormalize.forEach((key) => (updates[key] = 1));
        const attributes = pendingAttributes(cart, state);
        const hasUpdates = Object.keys(updates).length > 0;
        const hasAttributes = Object.keys(attributes).length > 0;
        const tried = boot ? readAdded(cart) : null;
        const additions = state.toAdd.filter((item) => !tried?.has(addKey(item))).filter(trackAttempt);
        if (!hasUpdates && !hasAttributes && !additions.length) {
          if (!pending) break;
          continue;
        }
        if (hasUpdates || hasAttributes) {
          await Theme.cart.update(updates, { source: 'gifts', toast: false, ...(hasAttributes ? { attributes } : {}) });
        }
        if (additions.length) {
          rememberAdded(cart, additions);
          try {
            await addGifts(additions);
          } catch (error) {
            for (const item of additions) {
              try {
                await addGifts([item]);
              } catch (innerError) {
                failedVariants.add(item.id);
              }
            }
          }
        }
      }
    } catch (error) {
      latestCart = null;
    } finally {
      running = false;
      setBusy(false);
      if (latestCart) updateMilestones(latestCart);
    }
  }

  document.addEventListener('cart:updated', (event) => {
    const { cart, source } = event.detail || {};
    if (cart) latestCart = cart;
    trackGifts(cart);
    updateMilestones(cart);
    if (source !== 'gifts') reconcile();
  });

  document.addEventListener('cart:rendered', (event) => {
    const cart = event.detail?.cart || latestCart;
    const root = event.detail?.root || document;
    updateMilestones(cart, root);
    restoreChoosers(root);
    runReveals(root);
    showChargedWarning(root);
  });

  document.addEventListener('cart:open', () => requestAnimationFrame(() => runReveals(document)));

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details?.matches?.('[data-gift-chooser-details]')) chooserOpen.set(details.dataset.tier, details.open);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-gift-remove]');
    if (remove) {
      event.preventDefault();
      if (!settings.giftsAllowRemove || remove.getAttribute('aria-busy') === 'true') return;
      remove.setAttribute('aria-busy', 'true');
      remove.closest('[data-cart-line]')?.setAttribute('aria-busy', 'true');
      setOptOut(remove.dataset.tier, true);
      reconcile();
      return;
    }
    const restore = event.target.closest('[data-gift-restore]');
    if (restore) {
      event.preventDefault();
      if (restore.getAttribute('aria-busy') === 'true') return;
      restore.setAttribute('aria-busy', 'true');
      setOptOut(restore.dataset.tier, false);
      reconcile();
      return;
    }
    const card = event.target.closest('[data-gift-choice]');
    if (!card || card.disabled) return;
    event.preventDefault();
    const { tier, variantId } = card.dataset;
    if (card.getAttribute('aria-pressed') === 'true' && !card.closest('[data-gift-chooser]')?.hasAttribute('aria-busy')) return;
    card.closest('[data-gift-chooser]')?.querySelectorAll('[data-gift-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === card));
    });
    setOptOut(tier, false);
    saveChoice(tier, variantId);
    reconcile();
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-gift-variant-select]');
    if (!select) return;
    saveChoice(select.dataset.tier, select.value);
    reconcile();
  });

  function boot() {
    const embedded = document.querySelector('[data-cart-json]');
    if (!latestCart && embedded) {
      try {
        latestCart = JSON.parse(embedded.textContent);
      } catch (error) {
        latestCart = null;
      }
    }
    restoreChoosers();
    runReveals();
    if (!latestCart && currencyRate() !== 1 && document.querySelector('[data-cart-milestones], [data-gift-chooser]')) {
      Theme.cart
        ?.get?.()
        .then((cart) => {
          latestCart = latestCart || cart;
          updateMilestones(latestCart);
        })
        .catch(() => {});
      return;
    }
    if (!latestCart) return;
    trackedCart = trackedCart || latestCart;
    updateMilestones(latestCart);
    if (Theme.abOff?.('gift_bar')) return;
    const state = stateFor(latestCart);
    const attributes = Object.keys(state.picks).some(
      (key) => parsePickAttribute(latestCart.attributes?.[`${PICK_ATTRIBUTE_PREFIX}${key}`]) !== state.picks[key]
    );
    const tried = readAdded(latestCart);
    const adds = state.toAdd.filter((item) => !tried.has(addKey(item)));
    if (adds.length || state.toRemove.length || state.toNormalize.length || attributes) {
      latestCart = null;
      reconcile({ boot: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  return {
    reconcile,
    idle,
    isBusy: () => running,
    updateMilestones,
    saveChoice,
    setOptOut,
    readChoices: () => readChoices(tiers()),
    readPicks: () => readPicks(latestCart, tiers()),
    readOptOut: () => readOptOut(latestCart),
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.ConvertGifts?.reconcile) {
  const glue = initGifts();
  window.ConvertGifts = Object.assign(window.ConvertGifts || {}, {
    computeGiftState,
    milestoneMessage,
    formatShortMoney,
    isGiftLine,
    ...glue,
  });
}
